<script setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';

const props = defineProps({
  text: { type: String, default: '' },
  maxWidth: { type: String, default: 'unset' },
  minWidth: { type: String, default: 'unset' },
  lines: { type: Number, default: 1 },
});

const el = ref(null);
const overflowing = ref(false);

// The full text is only offered on hover when it is actually being cut off —
// a title on text that already fits is noise a screen reader reads twice.
const measure = () => {
  const node = el.value;
  if (!node) return;
  overflowing.value = props.lines > 1
    ? node.scrollHeight > node.clientHeight + 1
    : node.scrollWidth > node.clientWidth + 1;
};

let observer;

onMounted(() => {
  measure();
  if (typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(measure);
    observer.observe(el.value);
  }
});

onBeforeUnmount(() => observer?.disconnect());

watch(() => props.text, () => requestAnimationFrame(measure));
</script>

<template>
  <span
    ref="el"
    class="xl-truncate"
    :class="{ 'xl-truncate--clamp': props.lines > 1 }"
    :style="{ maxWidth: props.maxWidth, minWidth: props.minWidth, '--xl-truncate-lines': props.lines }"
    :title="overflowing ? props.text : undefined"
  >{{ props.text }}<slot /></span>
</template>

<style scoped>
.xl-truncate, .xl-truncate * { box-sizing: border-box; }

.xl-truncate {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.xl-truncate--clamp {
  display: -webkit-box;
  -webkit-line-clamp: var(--xl-truncate-lines);
  line-clamp: var(--xl-truncate-lines);
  -webkit-box-orient: vertical;
  white-space: normal;
}
</style>
